
import { registerLocaleData } from '@angular/common';
import { Location } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeAU from '@angular/common/locales/en-AU';
import { LOCALE_ID, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NoPreloading, PreloadAllModules, RouterModule, Routes, TitleStrategy } from '@angular/router';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import { FullCalendarModule } from '@fullcalendar/angular';
import { TenantStatus, INDEXDB_STORE_WIDGET, PaymentType, LocaleSettings, ActivityEntityName } from '@jms/model';
import { AuthorizeGuard } from '@jms/tenant/module.jms.authorisation';
import { CoreDataModule } from '@jms/tenant/module.jms.core.data';
import { NgrxActionsModule } from '@jms/shared/ngrx-actions';
import { NgrxSelect } from '@jms/shared/ngrx-actions';
import { AvatarModule } from '@jms/ui/ui.avatar';
import { ConfirmModule } from '@jms/ui/ui.confirm';
import { AccessDeniedComponent, ErrorPageModule } from '@jms/ui/ui.error.pages';
import { UploaderModule, uploaderReducer } from '@jms/ui/ui.uploader';
import { AuthenticationInterceptor } from '@jms/utils/util.authentication/src/authentication.interceptor';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';;
import { PipeModule } from '@jms/utils/util.pipes';
import { ReduxModule } from '@jms/utils/util.redux';
import { SentryModule } from '@jms/utils/util.sentry.io';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { AppComponent } from './components/app/app.component';
import { AuthoriseComponent } from './components/authorise/authorise.component';
import { InactiveTenantComponent } from './components/inactive-tenant/inactive.tenant.component';
import { InactiveUserComponent } from './components/inactive-user/inactive.user.component';
import { ShellComponent } from './components/shell/shell.component';
import { SideNavComponent } from './components/sidenav/sidenav.component';
import { AuthGuard } from './guards/auth.guard'
import { HomeGuard } from './guards/home.guard';
import { FaviconModule, FAVICON_CONFIG } from '@jms/ui/ng-favicon';
import { DBConfig, NgxIndexedDBModule } from 'ngx-indexed-db';

import { CODE_PICKER_SERVICE } from '@jms/ui/ui.code.picker.pro';
import { PortalModule } from '@angular/cdk/portal';
import { OverlayModule } from '@angular/cdk/overlay';
import { SideNavMenuComponent } from './components/sidenav/sidenav.menu.component';
import { SideNavItemComponent } from './components/sidenav/sidenav.item.component';
import { NgxStripeModule } from 'ngx-stripe';
import { NgMapsCoreModule } from '@ng-maps/core';

import { NgMapsGoogleModule, GOOGLE_MAPS_API_CONFIG } from '@ng-maps/google';
import { provideQueryDevTools } from '@ngneat/query-devtools';

import { MenuModule } from 'primeng/menu';
import { CustomPageTitleStrategy } from '@workbuddy/ui/services';
import {
    ModuleRegistry,
    AllCommunityModule,
    ClientSideRowModelModule,
    GroupFilterModule,
    PivotModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    TreeDataModule,
} from 'ag-grid-enterprise';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { ApplicationEffects, applicationReducer } from '@jms/tenant/module.jms.state';
import { CodePickerService } from '@jms/ui/ui.standalone.services/code.picker.service';
import { HelpNavComponent } from './components/help.nav/help.nav.component';
import { UserNavComponent } from './components/user.nav/user.nav.component';
import { HelpService } from './components/help.nav/services';
import { ToastModule } from 'primeng/toast';
import { AlertComponent } from '@workbuddy/ui/components/alert';

registerLocaleData(localeAU, 'en-AU');
LocaleSettings.setLocale(ConfigService?.environment?.locale ?? 'en-AU');

const isPreviewOrPublic = window.location.href.indexOf('forms') > -1 || window.location.href.indexOf('preview') > -1 || window.location.href.indexOf('public') > -1;

const app_routes: Routes = [
    {
        path: '',
        component: ShellComponent,
        canActivate: [AuthGuard],
        children: [

            // {
            //     path: 'home2',
            //     canActivate: [HomeGuard],
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.home.dashboard').then(m => m.HomeDashboardModule),
            //     data: { title: 'Home', animation: 'homePage', restrictions: [TenantStatus.Ready] }
            // },
            {
                path: 'home',
                canActivate: [HomeGuard],
                loadChildren: () => import('../../../../libs/tenant/module.jms.dashboard').then(m => m.JmsDashboardModule),
                data: { title: 'Home', icon: 'fa-home', animation: 'homePage', restrictions: [TenantStatus.Ready] }
            },
            // {
            //     path: 'contacts',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.contacts').then(m => m.ContactsModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Contacts', icon: 'fa-address-card', restrictions: [TenantStatus.Ready] }
            // },
            {
                path: 'crm',
                canActivate: [AuthorizeGuard],
                data: { title: 'CRM', icon: 'fa-address-book-o', restrictions: [TenantStatus.Ready] },
                loadChildren: () => import('../../../../libs/tenant/module.jms.crm').then(m => m.JmsCrmModule),
            },
            // {
            //     path: 'crmpro',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.crm.pro').then(m => m.CrmShellModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'CRM Pro', icon: 'fa-address-card', restrictions: [TenantStatus.Ready] }
            // },
            {
                path: 'compliance',
                loadChildren: () => import('../../../../libs/shared/module.individual.compliance').then(m => m.JmsIndividualComplianceModule),
                canActivate: [AuthorizeGuard],
                data: { title: 'Compliance', icon: 'fa-certificate', restrictions: [TenantStatus.Ready] }
            },
            {
                path: 'timesheets',
                loadChildren: () => import('../../../../libs/tenant/module.jms.timesheets.pro').then(m => m.ManualTimesheetsModule),
                canActivate: [AuthorizeGuard],
                data: { title: 'Timesheets', icon: 'fa-clock-o', restrictions: [TenantStatus.Ready] }
            },
            // {
            //     path: 'quotes',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.quotes').then(m => m.QuoteModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Quotes', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
            // },
            // {
            //     path: 'quotes-pro',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.quotes.pro').then(m => m.JmsQuotesProModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Quotes Pro', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
            // },
            // {
            //     path: 'assets',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.asset.register').then(m => m.AssetRegisterModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Assets', icon: 'icomoon icomoon-warehouse', restrictions: [TenantStatus.Ready] }
            // },
            {
                path: 'assets',
                loadChildren: () => import('@workbuddy/modules/boards/equipment').then(m => m.EQUIPMENT_MODULE_ROUTES),
                canActivate: [AuthorizeGuard],
                data: {
                    title: 'Assets',
                    icon: 'fa-solid fa-warehouse-full',
                    restrictions: [TenantStatus.Ready],
                },
            },
            // {
            //     path: 'assets-1',
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Assets', icon: 'fa-solid fa-warehouse-full', restrictions: [TenantStatus.Ready] },
            //     children: [
            //         // {
            //         //     path: 'assets-old',
            //         //     loadChildren: () => import('../../../../libs/tenant/module.jms.asset.register').then(m => m.AssetRegisterModule),
            //         //     canActivate: [AuthorizeGuard],
            //         //     data: { title: 'Assets - Old', icon: 'icomoon icomoon-warehouse', restrictions: [TenantStatus.Ready] }
            //         // },
            //         {
            //             path: 'customer-assets',
            //             canActivate: [AuthorizeGuard],
            //             loadChildren: () => import('@workbuddy/modules/boards/assets/assets.routes').then(m => m.ASSETS_ROUTES),
            //             data: { title: 'Customer Assets', icon: 'fa-solid fa-warehouse-full', restrictions: [TenantStatus.Ready] }

            //         },
            //         {
            //             path: 'internal-equipment',
            //             canActivate: [AuthorizeGuard],
            //             loadChildren: () => import('@workbuddy/modules/boards/equipment/equipment.routes').then(m => m.EQUIPMENT_ROUTES),
            //             data: {
            //                 title: 'Internal Equipment',
            //                 icon: 'fa-solid fa-toolbox',
            //                 restrictions: [TenantStatus.Ready],
            //                 context: {
            //                     title: 'Internal Equipment',
            //                     entity: ActivityEntityName.EquipmentInternal,
            //                 }
            //             }
            //         },
            //         {
            //             path: 'equipment',
            //             canActivate: [AuthorizeGuard],
            //             loadChildren: () => import('@workbuddy/modules/boards/equipment').then(m => m.EQUIPMENT_VEHICLE_ROUTES),
            //             data: {
            //                 title: 'Equipment',
            //                 icon: 'fa-solid fa-inventory',
            //                 restrictions: [TenantStatus.Ready],
            //                 context: {
            //                     title: 'Equipment',
            //                     entity: ActivityEntityName.EquipmentHireable,
            //                 }
            //             }
            //         },
            //         {
            //             path: 'vehicles',
            //             canActivate: [AuthorizeGuard],
            //             loadChildren: () => import('@workbuddy/modules/boards/equipment').then(m => m.EQUIPMENT_VEHICLE_ROUTES),
            //             data: {
            //                 title: 'Vehicles',
            //                 icon: 'fa-solid fa-car',
            //                 restrictions: [TenantStatus.Ready],
            //                 context: {
            //                     title: 'Vehicles',
            //                     entity: ActivityEntityName.EquipmentVehicle,
            //                 }
            //             }
            //         },
            //         // {
            //         //     path: 'serial-numbers',
            //         //     canActivate: [AuthorizeGuard],
            //         //     loadChildren: () => import('@workbuddy/modules/boards/equipment.serialised').then(m => m.EQUIPMENT_SERIALISED_ROUTES),
            //         //     data: { title: 'Serial Numbers', icon: 'fa-solid fa-toolbox', restrictions: [TenantStatus.Ready] }
            //         // },
            //         {
            //             path: 'stock-locations',
            //             canActivate: [AuthorizeGuard],
            //             loadChildren: () => import('@workbuddy/modules/boards/stock.locations').then(m => m.STOCK_LOCATION_ROUTES),
            //             data: { title: 'Stock Locations', icon: 'fa-solid fa-pallet-boxes', restrictions: [TenantStatus.Ready] }
            //         }
            //     ]
            // },

            {
                path: 'quotes',
                canActivate: [AuthorizeGuard],
                data: { title: 'Quotes', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] },
                children: [
                    // {
                    //     path: 'quotes-deprecated',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.quotes').then(m => m.QuoteModule),
                    //     canActivate: [AuthorizeGuard],
                    //     data: { title: 'Quotes [Deprecated]', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
                    // },
                    {
                        path: 'quotes-new',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.quotes.pro').then(m => m.JmsQuotesProModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Quotes', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'quote-templates',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.quote.templates').then(m => m.QuoteTemplatesModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Quote Templates', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
                    },
                ]
            },

            {
                path: 'sales',
                canActivate: [AuthorizeGuard],
                data: { title: 'Sales', icon: 'fa-money', restrictions: [TenantStatus.Ready] },
                children: [
                    {
                        path: 'leads',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.leads').then(m => m.JmsLeadsModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Leads', icon: 'fa-users', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'opportunities',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.opportunities').then(m => m.JmsOpportunitiesModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Opportunities', icon: 'fa-handshake-o', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'quotes-new',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.quotes.pro').then(m => m.JmsQuotesProModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Quotes', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'quote-templates',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.quote.templates').then(m => m.QuoteTemplatesModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Quote Templates', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
                    },
                ]
            },

            {
                path: 'jobs',
                data: { title: 'Operations', icon: 'icomoon icomoon-jobs', restrictions: [TenantStatus.Ready] },
                children: [
                    // {
                    //     path: 'projects',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.projects').then(m => m.JmsProjectsModule),
                    //     canActivate: [AuthorizeGuard],
                    //     data: { title: 'Projects', icon: 'fa-tasks', restrictions: [TenantStatus.Ready] }
                    // },
                    {
                        path: 'operations',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.operations').then(m => m.OperationsModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Jobs', icon: 'fa-tachometer', restrictions: [TenantStatus.Ready] }
                    },
                    // {
                    //     path: 'recurring',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.recurring.jobs').then(m => m.RecurringJobsModule),
                    //     canActivate: [AuthorizeGuard],
                    //     data: { title: 'Recurring Jobs', icon: 'icomoon icomoon-recurring-jobs', restrictions: [TenantStatus.Ready] }
                    // },
                    {
                        path: 'recurring',
                        loadChildren: () => import('@workbuddy/modules/boards/recurring.jobs/recurring.jobs.routes').then(m => m.RECURRING_JOBS_ROUTES),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Recurring Jobs', icon: 'icomoon icomoon-recurring-jobs', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'job-templates',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.job.templates').then(m => m.JobTemplatesModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Job Templates', icon: 'icomoon icomoon-job-template', restrictions: [TenantStatus.Ready] }
                    },
                    // {
                    //     path: 'project-templates',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.project.templates').then(m => m.ProjectTemplatesModule),
                    //     canActivate: [AuthorizeGuard],
                    //     data: { title: 'Project Templates', icon: 'icomoon icomoon-job-template', restrictions: [TenantStatus.Ready] }
                    // },
                    {
                        path: 'resource-location',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.resource.location').then(m => m.ResourceLocationModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Resource Location', icon: 'fa-map-marker', restrictions: [TenantStatus.Ready] }
                    },
                    // {
                    //     path: 'daily-logs',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.daily.logs').then(m => m.JmsDailyLogModule),
                    //     canActivate: [AuthorizeGuard],
                    //     data: { title: 'Daily Logs', icon: 'fa-cogs', restrictions: [TenantStatus.Ready] }
                    // },

                    {
                        path: 'homes-nsw-notices',
                        loadChildren: () => import('@workbuddy/modules/boards/homes.nsw.notices/homes.nsw.notices.routes').then(m => m.HOMES_NSW_NOTICES_ROUTES),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Notices', icon: 'fa-flag-swallowtail', restrictions: [TenantStatus.Ready] }
                    },

                    {
                        path: 'view',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.job.details').then(m => m.JobDetailsModule),
                        data: { title: 'Job Details', type: 'Job' }
                    },
                ]
            },
            {
                path: 'locations',
                loadComponent: () => import('@workbuddy/ui/modules/locations/locations.dashboard.component').then(m => m.LocationsDashboardComponent),
                canActivate: [AuthorizeGuard],
                data: { title: 'Locations', icon: 'fa-map-marker', restrictions: [TenantStatus.Ready] }
            },
            {
                path: 'works',
                loadChildren: () => import('@workbuddy/modules/boards/works').then(m => m.WORK_ROUTES),
                canActivate: [AuthorizeGuard],
                data: { title: 'Job v2', icon: 'fa-cube', restrictions: [TenantStatus.Ready] }
            },
            // {
            //     path: 'operations',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.operations').then(m => m.OperationsModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Operations', icon: 'fa-tachometer', restrictions: [TenantStatus.Ready] }
            // },



            // {
            //     path: 'scheduling',
            //     data: { title: 'Scheduling', icon: 'fa-calendar', restrictions: [TenantStatus.Ready] },
            //     children: [
            //         // {
            //         //     path: 'allocations',
            //         //     loadChildren: () => import('../../../../libs/tenant/module.jms.scheduling.allocations').then(m => m.JmsJobAllocationsModule),
            //         //     canActivate: [AuthorizeGuard],
            //         //     data: { title: 'Job Allocations', icon: 'fa fa-calendar', restrictions: [TenantStatus.Ready] }
            //         // },
            //         {
            //             path: 'allocations',
            //             loadChildren: () => import('../../../../libs/tenant/module.jms.scheduling.allocations.pro').then(m => m.JmsJobAllocationsProModule),
            //             canActivate: [AuthorizeGuard],
            //             data: { title: 'Job Allocations', icon: 'fa-calendar', restrictions: [TenantStatus.Ready] }
            //         },
            //         // {
            //         //     path: 'appointments',
            //         //     loadChildren: () => import('../../../../libs/module.jms.scheduling.appointment/core').then(m => m.AppointmentCoreModule),
            //         //     canActivate: [AuthorizeGuard],
            //         //     data: { title: 'Appointments', icon: 'fa-calendar', restrictions: [TenantStatus.Ready] }
            //         // },
            //         {
            //             path: 'runsheets',
            //             loadChildren: () => import('../../../../libs/tenant/module.jms.scheduling.runsheet/core').then(m => m.SchedulingCoreModule),
            //             canActivate: [AuthorizeGuard],
            //             data: { title: 'Runsheets', icon: 'fa-truck', restrictions: [TenantStatus.Ready] }
            //         },
            //     ]
            // },

            {
                path: 'scheduler',
                data: { title: 'Scheduler', icon: 'fa-calendar', restrictions: [TenantStatus.Ready] },
                canActivate: [AuthorizeGuard],
                loadChildren: () => import('../../../../libs/tenant/module.jms.scheduling.allocations.pro').then(m => m.JmsJobAllocationsProModule),
            },

            {
                path: 'finance',
                data: { title: 'Finance', icon: 'fa-money-bills', restrictions: [TenantStatus.Ready] },
                children: [
                    {
                        path: 'pending',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.finance').then(m => m.FinanceModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Pending', icon: 'fa-clock-o', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'invoices',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.invoices').then(m => m.InvoicesModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Invoices', icon: 'fa-money-bills', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'invoices-consolidation',
                        loadComponent: () => import('../../../../workbuddy/modules/boards/bulk.invoices').then(m => m.BulkInvoicesDashboardComponent),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Consolidated Invoices', icon: 'fa-solid fa-file-invoice-dollar', restrictions: [TenantStatus.Ready], topLevel: true }
                    },
                    {
                        path: 'bills',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.bills').then(m => m.BillsModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Bills', icon: 'fa-money', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'accounts-receivable',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.payments').then(m => m.PaymentsModule),
                        canLoad: [AuthorizeGuard],
                        data: { title: 'Payments', type: PaymentType.Receivable, icon: 'fa-credit-card', restrictions: [TenantStatus.Ready] }
                    },
                    // {
                    //     path: 'accounts-payable',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.payments').then(m => m.PaymentsModule),
                    //     canLoad: [AuthorizeGuard],
                    //     data: { title: 'Accounts Payable', type: PaymentType.Payable, icon: 'fa-credit-card', restrictions: [TenantStatus.Ready] }
                    // },
                    // {
                    //     path: 'reconcile-payments',
                    //     loadChildren: () => import('../../../../libs/tenant/module.jms.payments.reconciliation').then(m => m.ReconciliationModule),
                    //     canLoad: [AuthorizeGuard],
                    //     data: { title: 'Reconcile Payments', icon: 'fa-balance-scale', restrictions: [TenantStatus.Ready] }
                    // },
                    {
                        path: 'payment-sync',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.payment.sync').then(m => m.PaymentSyncModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Payment Sync', icon: 'fa-cloud-download', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'sales-order',
                        loadChildren: () => import('../../../../libs/tenant/module.jms.sales.order').then(m => m.SaleOrderModule),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Sales Order', icon: 'fa-money-check-dollar', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'purchase-orders',
                        loadChildren: () => import('@workbuddy/modules/boards/purchase.orders').then(m => m.PURCHASE_ORDERS_MODULE_ROUTES),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Purchase Orders', icon: 'fa-shopping-cart', restrictions: [TenantStatus.Ready]

                        }
                    },
                ]
            },
            {
                path: 'finance-pro',
                data: { title: 'Finance', icon: 'fa-money-bills', restrictions: [TenantStatus.Ready] },
                children: [
                    {
                        path: 'invoices',
                        loadComponent: () => import('@workbuddy/modules/boards/invoices').then(m => m.InvoicesDashboardComponent),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Invoices', icon: 'fa-money-bills', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'bills',
                        loadComponent: () => import('@workbuddy/modules/boards/bills').then(m => m.BillsDashboardComponent),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Bills', icon: 'fa-money', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'quotes',
                        loadComponent: () => import('@workbuddy/modules/boards/quotes').then(m => m.QuotesDashboardComponent),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Quotes', icon: 'fa-calculator', restrictions: [TenantStatus.Ready] }
                    },
                    {
                        path: 'opportunities',
                        loadComponent: () => import('@workbuddy/modules/boards/opportunities').then(m => m.OpportunitiesDashboardComponent),
                        canActivate: [AuthorizeGuard],
                        data: { title: 'Opportunities', icon: 'fa-handshake-o', restrictions: [TenantStatus.Ready] }
                    },
                    // {
                    //     path: 'purchase-orders',
                    //     loadComponent: () => import('@workbuddy/modules/boards/purchase.orders').then(m => m.PurchaseOrdersDashboardComponent),
                    //     canActivate: [AuthorizeGuard],
                    //     data: { title: 'Purchase Orders', icon: 'fa-shopping-cart', restrictions: [TenantStatus.Ready] }
                    // },

                    {
                        path: 'purchase-orders',
                        loadChildren: () => import('@workbuddy/modules/boards/purchase.orders').then(m => m.PURCHASE_ORDERS_MODULE_ROUTES),
                        canActivate: [AuthorizeGuard],
                        data: {
                            title: 'Purchase Orders', icon: 'fa-shopping-cart', restrictions: [TenantStatus.Ready]

                        }
                    },
                ]
            },
            // {
            //     path: 'purchase',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.purchase.orders').then(m => m.PurchaseOrdersModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Purchase Orders', icon: 'fa-shopping-cart', restrictions: [TenantStatus.Ready] }
            // },

            // {
            //     path: 'setup',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.tenant.setup/src/tenant.setup.module').then(m => m.TenantSetupModule),
            //     canActivate: [AuthorizeGuard],
            //     data: {
            //         title: 'Setup Wizard',
            //         icon: 'fa-magic',
            //         animation: 'homePage',
            //         restrictions: [TenantStatus.Confirmed, TenantStatus.Initializing, TenantStatus.Ready]
            //     }
            // },
            // {
            //     path: 'knowledgebase',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.knowledge.base').then(m => m.JmsKnowledgeBaseModule),
            //     canActivate: [AuthorizeGuard],
            //     data: { title: 'Knowledge Base', icon: 'fa-graduation-cap', restrictions: [TenantStatus.Ready] }
            // },
            {
                path: 'reports',
                loadChildren: () => import('../../../../libs/tenant/module.jms.reporting').then(m => m.ReportingModule),
                canActivate: [AuthorizeGuard],
                data: { title: 'Reports', icon: 'fa-bar-chart', restrictions: [TenantStatus.Ready] }
            },
            {
                path: 'settings',
                loadChildren: () => import('../../../../libs/tenant/module.jms.settings.shared').then(m => m.SettingsSharedModule),
                canActivate: [AuthorizeGuard],
                data: { title: 'Settings', icon: 'fa-cog', restrictions: [TenantStatus.Ready] }
            },
            // {
            //     path: 'formviewer',
            //     loadChildren: () => import('../../../../libs/tenant/module.jms.form.responses').then(m => m.FormResponseModule),
            //     data: { restrictions: [TenantStatus.Ready] }
            // },
            { path: '', redirectTo: 'home', pathMatch: 'full' },
        ]
    },
    { path: 'forms', loadChildren: () => import('../../../../libs/tenant/module.jms.form.responses').then(m => m.FormResponseModule) },

    { path: 'callback', component: AuthoriseComponent },
    { path: 'inactive/user', component: InactiveUserComponent },
    { path: 'inactive/tenant', component: InactiveTenantComponent },
    { path: 'login/access-denied', component: AccessDeniedComponent },
    {
        path: 'preview',
        children: [
            {
                path: 'quote',
                loadChildren: () => import('../../../../libs/tenant/module.jms.document.themes').then(m => m.QuoteViewerModule)
            },
            {
                path: 'invoice',
                loadChildren: () => import('../../../../libs/tenant/module.jms.document.themes').then(m => m.InvoiceViewerModule)
            },
            {
                path: 'bill',
                loadChildren: () => import('../../../../libs/tenant/module.jms.document.themes').then(m => m.BillViewerModule)
            },
            {
                path: 'sale-order',
                loadChildren: () => import('../../../../libs/tenant/module.jms.document.themes').then(m => m.SaleOrderViewerModule)
            },
            {
                path: 'po/:id',
                loadComponent: () => import('../../../../libs/tenant/module.jms.document.themes').then(m => m.PurchaseOrderPreviewComponent)
            },
            {
                path: '',
                component: AccessDeniedComponent
            }
        ]
    },
    {
        path: 'public',
        children: [
            {
                path: 'quotes',
                loadChildren: () => import('../../../../libs/tenant/module.jms.public.quotes').then(m => m.PublicQuotesModule),
            },
            {
                path: 'invoices',
                loadChildren: () => import('../../../../libs/tenant/module.jms.public.invoices').then(m => m.PublicInvoicesModule),
            },
            {
                path: '',
                redirectTo: 'quotes',
                pathMatch: 'full'
            }
        ]
    }

] as Routes;

const storeConfig = {
    runtimeChecks: {
        strictStateSerializability: false,
        strictStateImmutability: false,
        strictActionSerializability: false,
        strictActionImmutability: false,
    }
};


// #region IndexDB configuration

// Ahead of time compiles requires an exported function for factories
// export function migrationFactory() {
//     return {
//         1: (db: IDBDatabase, transaction: IDBTransaction) => {
//             const store = transaction.objectStore(INDEXDB_STORE_WIDGET);
//             store.createIndex('dashboardId', 'dashboardId', { unique: false });
//         },
//     };
// }

const indexDbConfig: DBConfig = {
    name: 'JmsDb',
    version: 1,
    objectStoresMeta: [{
        store: INDEXDB_STORE_WIDGET,
        storeConfig: { keyPath: 'id', autoIncrement: false },
        storeSchema: [
            // { name: 'dashboardId', keypath: 'dashboardId', options: { unique: false } },
            { name: 'data', keypath: 'data', options: { unique: false } },
            { name: 'expired', keypath: 'expired', options: { unique: false } },
        ]
    }],
    // // provide the migration factory to the DBConfig
    // migrationFactory
};

// #endregion

ModuleRegistry.registerModules([
    AllCommunityModule,
    ClientSideRowModelModule,
    GroupFilterModule,
    PivotModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    TreeDataModule
]);


@NgModule({
    declarations: [
        AppComponent,
        ShellComponent,
        SideNavComponent,
        SideNavItemComponent,
        SideNavMenuComponent,
        InactiveUserComponent,
        InactiveTenantComponent,
        AuthoriseComponent,
        HelpNavComponent,
        UserNavComponent,
    ],
    bootstrap: [
        AppComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        FullCalendarModule,
        SentryModule.forRoot(),
        JwtModule.forRoot({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useFactory: () => {
                    const config = {
                        authScheme: '',
                        tokenGetter: () => localStorage.getItem('auth_token'),
                        skipWhenExpired: true,
                        allowedDomain: new Array(new RegExp('^null$'))
                    };
                    return config;
                }
            }
        }),
        NgxIndexedDBModule.forRoot(indexDbConfig),
        LoadingBarHttpClientModule,
        // LoggingModule.forRoot(),
        StoreModule.forRoot({
            application: applicationReducer,
            uploader: uploaderReducer
        }, storeConfig),
        StoreDevtoolsModule.instrument({ maxAge: 10 }),
        StoreRouterConnectingModule.forRoot({
            stateKey: 'application', // name of reducer key
        }),
        EffectsModule.forRoot([ApplicationEffects]),
        NgrxActionsModule,
        DialogModule,
        ProgressBarModule,
        ToastModule,
        AlertComponent,
        PipeModule,
        CoreDataModule.forRoot(),
        AvatarModule,
        ReduxModule.forRoot(),
        ErrorPageModule,
        ConfirmModule,
        UploaderModule,
        FaviconModule,
        PortalModule,
        OverlayModule,
        NgxStripeModule.forRoot(ConfigService.environment.stripe),
        NgMapsCoreModule,
        NgMapsGoogleModule,
        MonacoEditorModule.forRoot(),
        MenuModule,
        RouterModule.forRoot(app_routes, {
            preloadingStrategy: isPreviewOrPublic ? NoPreloading : PreloadAllModules,
            bindToComponentInputs: true
        })
    ],
    providers: [
        { provide: LOCALE_ID, useValue: 'en-AU' },
        {
            provide: HTTP_INTERCEPTORS,
            useFactory: (location: Location) => {
                return new AuthenticationInterceptor(location);
            },
            multi: true,
            deps: [Location]
        },
        AuthGuard,
        AuthorizeGuard,
        MessageService,
        {
            provide: FAVICON_CONFIG,
            useValue: {
                bgColor: '#888',
            }
        },
        { provide: CODE_PICKER_SERVICE, useClass: CodePickerService },
        {
            provide: GOOGLE_MAPS_API_CONFIG,
            useValue: {
                apiVersion: 'beta',
                libraries: ['places', 'geometry', 'marker'],
                apiKey: ConfigService.environment.mappingKey
            }
        },
        {
            provide: TitleStrategy, useClass: CustomPageTitleStrategy
        },
        ConfigService.environment.environment === 'dev' && !isPreviewOrPublic
            ? provideQueryDevTools({
                buttonPosition: 'bottom-left'
            })
            : [],
        provideHttpClient(withInterceptorsFromDi()),
        HelpService,

    ]
})
export class ApplicationModule {
    constructor(
        store: Store<any>,
        ngrxSelect: NgrxSelect,
    ) {
        ngrxSelect.connect(store);
    }
}
